import React, { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { FaQuoteLeft } from "react-icons/fa";

import frame1 from "@/assets/images/overviewImage1.png";
import frame2 from "@/assets/images/overviewImage2.png";
import frame3 from "@/assets/images/overviewImage3.png";
import frame4 from "@/assets/images/overviewImage4.png";

gsap.registerPlugin(ScrollTrigger);

const Hero: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const line1Ref = useRef<HTMLSpanElement>(null);
  const line2Ref = useRef<HTMLSpanElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      !sectionRef.current ||
      !line1Ref.current ||
      !line2Ref.current ||
      !gridRef.current
    )
      return;

    gsap.set(gridRef.current, { autoAlpha: 0 });

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: sectionRef.current,
        start: "top 50%",
        end: "bottom 50%",
         toggleActions: "play none none none",
        once: true, 
      },
    });

    tl.fromTo(
      line1Ref.current,
      { x: -300, opacity: 0 },
      { x: 0, opacity: 1, duration: 1, ease: "power2.out" }
    )
      .fromTo(
        line2Ref.current,
        { x: -300, opacity: 0 },
        { x: 0, opacity: 1, duration: 1, ease: "power2.out" },
        "+=0.2"
      )
      .to(
        gridRef.current,
        { autoAlpha: 1, y: 0, duration: 0.8, ease: "power2.out" },
        "+=0"
      )
      .to(
        gridRef.current.querySelectorAll(".tw-filter"),
        { filter: "grayscale(1)", duration: 0.5, ease: "power2.out" },
        "+=5"
      );

    return () => {
      tl.scrollTrigger?.kill();
      tl.kill();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="tw-bg-black tw-py-16 tw-px-6 lg:tw-px-12 tw-relative tw-min-h-screen"
    >
        <img src="/overview/overviewsvg.svg" className="tw-absolute tw-left-auto -tw-z-10"/>
      {/* Heading */}
      <h1 className="tw-text-3xl md:tw-text-5xl lg:tw-text-7xl tw-font-bold tw-relative tw-text-white tw-pt-8 md:tw-pt-12 tw-pl-[15%]">
        <span ref={line1Ref} className="tw-block tw-mb-2">
          Good for business.
        </span>
        <span ref={line2Ref} className="tw-block">
          Great for customers.
        </span>
      </h1>

      {/* Horizontal scroll grid */}
      <div className="tw-overflow-x-auto tw-relative tw--mx-6 sm:tw-mx-0">
        <div
          ref={gridRef}
          className="tw-grid tw-grid-flow-col tw-auto-cols-[minmax(280px,1fr)] tw-gap-8 tw-px-6 sm:tw-px-0"
        >
          {/* Column 1 – Layout 1 */}
          <div className="tw-grid tw-grid-rows-3 tw-gap-6 tw-filter tw-relative tw-py-48 md:tw-py-64 lg:tw-py-72">
            <img
              src={frame1}
              alt="Overview Frame 1"
              className="tw-row-span-2 tw-rounded-2xl tw-object-cover tw-w-full tw-h-full"
            />
            <div className="tw-row-span-1 tw-bg-[#D357FF] tw-filter hover:tw-grayscale-0 tw-transition-all tw-duration-700 tw-rounded-2xl tw-p-6 tw-flex tw-flex-col tw-justify-center">
              <div className="tw-inline-block tw-mb-2">
                <span className="tw-text-3xl tw-font-bold tw-text-black">
                  Simplified solution
                </span>
              </div>
              <div className="tw-text-xl tw-font-normal tw-text-black">
                Automated claim support, AI-powered fraud mitigation, and more
              </div>
            </div>
          </div>

          {/* Column 2 – Layout 2 */}
          <div className="tw-grid tw-grid-rows-3 tw-gap-6 tw-py-32 md:tw-py-44 lg:tw-py-52">
            <div className="tw-row-span-1 tw-bg-[#FFBA33] tw-filter hover:tw-grayscale-0 tw-transition-all tw-duration-700 tw-rounded-2xl tw-p-6 tw-flex tw-flex-col tw-justify-center tw-items-start">
              <div className="tw-text-6xl md:tw-text-8xl tw-font-extrabold tw-text-black tw-leading-none">
                98%
              </div>
              <p className="tw-mt-2 tw-text-base md:tw-text-xl tw-text-black tw-font-medium">
                of claims processed in 90
                <br />
                seconds or less
              </p>
            </div>
            <img
              src={frame2}
              alt="Overview Frame 2"
              className="tw-row-span-1 tw-rounded-2xl tw-filter tw-object-cover tw-w-full tw-h-full"
            />
            <div className="tw-row-span-1 tw-bg-[#FFBA33] tw-filter hover:tw-grayscale-0 tw-transition-all tw-duration-700 tw-rounded-2xl tw-p-6 tw-flex tw-flex-col tw-justify-center">
              <div className="tw-text-3xl md:tw-text-4xl tw-font-bold tw-mb-2 tw-text-black">
                ₹24,000 crore
              </div>
              <span className="tw-text-xl tw-text-black tw-font-medium tw-leading-snug">
                Loss to Indian retailers due to fake returned claims,
                AI‑powered fraud mitigation, and more
              </span>
            </div>
          </div>

          {/* Column 3 – Layout 3 */}
          <div className="tw-grid tw-grid-rows-4 tw-filter tw-gap-6 tw-py-36 md:tw-py-48 lg:tw-py-64">
            <img
              src={frame3}
              alt="Overview Frame 3"
              className="tw-row-span-4 tw-rounded-2xl tw-filter tw-object-cover tw-w-full tw-h-full"
            />
          </div>

          {/* Column 4 – Layout 4 */}
          <div className="tw-grid tw-grid-rows-4 tw-gap-6 tw-py-28 md:tw-py-32 lg:tw-py-48">
            <img
              src={frame4}
              alt="Overview Frame 4"
              className="tw-row-span-2 tw-rounded-2xl tw-filter tw-object-cover tw-w-full tw-h-full"
            />
            <div className="tw-row-span-2 tw-bg-[#00FBBC] tw-filter hover:tw-grayscale-0 tw-transition-all tw-duration-700 tw-rounded-2xl tw-p-6 tw-flex tw-flex-col tw-justify-between">
              <div className="tw-text-7xl tw-leading-none tw-mb-6 tw-font-bold tw-text-black">
                <FaQuoteLeft />
              </div>
              <div className="tw-text-base md:tw-text-5xl tw-font-bold tw-text-black tw-mb-4">
                “The service level Protega has provided our team has been
                incredible.”
              </div>
              <div className="tw-text-xs tw-text-black tw-opacity-80 tw-py-6">
                Mehra Desai, President
                <br />
                Dufresne Furniture
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
